import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Trophy, Sparkles, Send, UserRound, AtSign, Ticket } from 'lucide-react';
import { floorToInt } from '../lib/utils';
import { resolveProfilePic, handleProfilePicError } from '../lib/avatar';

// `winner` may be a single announcement object OR a group object carrying a
// `winners` array (a two-winner day). Both are handled: on a two-winner day the
// user sees the winners one at a time — dismiss the first modal, the second
// appears; dismissing the last closes the popup.
const WinnerAnnouncementPopup = ({ isOpen, winner, onWinnerSeen, onClose }) => {
  const winners = winner?.winners?.length
    ? winner.winners
    : (winner ? [winner] : []);

  const [index, setIndex] = useState(0);

  // Reset to the first winner whenever a new announcement arrives.
  useEffect(() => {
    setIndex(0);
  }, [winner?.id, winners.length]);

  if (!winner || winners.length === 0) return null;

  const safeIndex = Math.min(index, winners.length - 1);
  const current = winners[safeIndex];
  const isLast = safeIndex >= winners.length - 1;
  const total = winners.length;

  const ticketNumber = current.ticketNumber ?? null;
  const ticketLabel = current.ticketLabel || (ticketNumber !== null ? `T : ${ticketNumber}` : null);

  const advance = async () => {
    if (current?.id && typeof onWinnerSeen === 'function') {
      await onWinnerSeen(current.id);
    }
    if (isLast) {
      if (typeof onClose === 'function') onClose();
    } else {
      setIndex((i) => i + 1);
    }
  };

  const closeAll = async () => {
    // Mark every remaining (unseen) winner seen, then close.
    if (typeof onWinnerSeen === 'function') {
      for (const w of winners.slice(safeIndex)) {
        if (w?.id) {
          // eslint-disable-next-line no-await-in-loop
          await onWinnerSeen(w.id);
        }
      }
    }
    if (typeof onClose === 'function') onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        // Wire X button (and Escape) to dismiss the whole popup, acknowledging
        // any winners the user did not click through.
        if (!open) {
          closeAll();
        }
      }}
    >
      <DialogContent className="max-w-md glass-panel border-white/40 overflow-hidden">
        <div className="absolute -top-10 -right-10 w-36 h-36 rounded-full bg-[#FBBF24]/20 blur-2xl" />
        <div className="absolute -bottom-10 -left-10 w-36 h-36 rounded-full bg-[#9333EA]/20 blur-2xl" />

        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-2xl text-[#000000] text-center flex items-center justify-center gap-2">
            <Trophy className="w-6 h-6 text-[#F59E0B]" />
            {total > 1 ? 'Heroes of the Day' : 'Hero of the Day'}
          </DialogTitle>
        </DialogHeader>

        {total > 1 && (
          <p
            className="text-center text-xs font-semibold text-[#9333EA] font-['DM_Sans'] -mt-2"
            data-testid="winner-progress-indicator"
          >
            Winner {safeIndex + 1} of {total}
          </p>
        )}

        <div className="relative z-10 space-y-4">
          <div className="flex justify-center">
            <img
              src={resolveProfilePic(current.profilePic)}
              alt={current.name}
              onError={handleProfilePicError}
              className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
            />
          </div>

          <div className="space-y-2 rounded-[16px] bg-white/70 border border-white/80 p-4">
            <div className="flex items-center gap-2 text-sm text-[#333333] font-['DM_Sans']">
              <UserRound className="w-4 h-4 text-[#9333EA]" />
              <span className="font-semibold">Name:</span>
              <span>{current.name}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-[#333333] font-['DM_Sans']">
              <AtSign className="w-4 h-4 text-[#9333EA]" />
              <span className="font-semibold">Username:</span>
              <span>{current.username}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-[#333333] font-['DM_Sans']">
              <Send className="w-4 h-4 text-[#0088cc]" />
              <span className="font-semibold">Telegram:</span>
              <span>{current.telegramId || 'N/A'}</span>
            </div>
            {ticketLabel && (
              <div
                className="flex items-center gap-2 text-sm text-[#333333] font-['DM_Sans']"
                data-testid="winner-ticket-number"
              >
                <Ticket className="w-4 h-4 text-[#16A34A]" />
                <span className="font-semibold">Ticket:</span>
                <span className="font-bold text-[#16A34A] font-['Outfit'] tracking-wide">{ticketLabel}</span>
              </div>
            )}
          </div>

          <div className="text-center rounded-[16px] p-4 bg-gradient-to-r from-[#FBBF24]/20 to-[#F59E0B]/20 border border-[#F59E0B]/25">
            <p className="text-sm text-[#5B3D00] font-['DM_Sans']">Profile Score Achieved</p>
            <p className="text-4xl font-bold text-[#8A5200] font-['Outfit']">{floorToInt(current.profileScoreWon ?? current.amountWon ?? 0)}</p>
          </div>

          <button
            onClick={advance}
            data-testid="winner-announcement-close"
            className="w-full bg-gradient-to-r from-[#9333EA] via-[#EC4899] via-[#FBBF24] to-[#EF4444] text-white py-3 rounded-full font-semibold flex items-center justify-center gap-2"
          >
            <Sparkles className="w-4 h-4" />
            {isLast ? 'Celebrate' : 'Next Winner'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WinnerAnnouncementPopup;
